import {
  AssetType,
  ContractorType,
  type Account,
} from '../../../../../types/api';
import { Asset, type AssetOption } from '../loan/asset';

// Pure helpers about the installer account — no UI involvement, no form
// state. Anything that asks "what type of installer is this?" lives here.

type InstallerAccount = Account | null | undefined;

// Energy contractor types do not count as Flex. Any other contractor type does,
// including the existing Flex value and later non-energy types.
const ENERGY_CONTRACTOR_TYPES = new Set<string>([
  ContractorType.SOLAR,
  ContractorType.STORAGE,
  ContractorType.EV_CHARGERS,
]);

// The asset cards each contractor type offers. Add a line here when a
// contractor type gets its own card, and take that asset out of Flex.
const ASSETS_BY_CONTRACTOR_TYPE: Partial<
  Record<string, ReadonlyArray<AssetType>>
> = {
  [ContractorType.SOLAR]: [AssetType.SOLAR],
  [ContractorType.STORAGE]: [AssetType.ENERGY_STORAGE_BESS],
  [ContractorType.EV_CHARGERS]: [AssetType.EV_CHARGERS],
  [ContractorType.FLEX]: [
    AssetType.ROOFING,
    AssetType.HVAC,
    AssetType.LED_LIGHTING,
    AssetType.OTHER,
  ],
};

const getContractorTypes = (account: InstallerAccount): string[] => {
  const contractorType = account?.contractor_type;
  if (!contractorType?.trim()) return [];
  return contractorType
    .split(';')
    .map((cType) => cType.trim())
    .filter(Boolean);
};

/** True when the account has a Flex-group contractor type (not Energy). */
export const hasFlex = (account: InstallerAccount): boolean =>
  getContractorTypes(account).some(
    (cType) => !ENERGY_CONTRACTOR_TYPES.has(cType)
  );

/** Asset types the account's contractor types allow. Empty defaults to Solar. */
export const getAssetTypesByContractorType = (
  account: InstallerAccount
): ReadonlyArray<AssetType> => {
  const contractorTypes = getContractorTypes(account);
  const effectiveContractorTypes =
    contractorTypes.length > 0 ? contractorTypes : [ContractorType.SOLAR];
  const assetTypesFromContractorTypes = new Set(
    effectiveContractorTypes.flatMap(
      (cType) => ASSETS_BY_CONTRACTOR_TYPE[cType] ?? []
    )
  );

  return Asset.OPTIONS.filter((option) =>
    assetTypesFromContractorTypes.has(option.assetType)
  ).map((option) => option.assetType);
};

/** Card options for the allowed asset types, in the standard asset order. */
export const getAssetOptionsByContractorType = (
  account: InstallerAccount
): ReadonlyArray<AssetOption> => {
  const assetTypes = new Set(getAssetTypesByContractorType(account));
  return Asset.OPTIONS.filter((option) => assetTypes.has(option.assetType));
};

/**
 * True when contractor_type includes "Solar" — and legacy / unset accounts
 * default to Solar per the SF convention.
 */
export const hasSolar = (account: InstallerAccount): boolean => {
  const contractorType = account?.contractor_type;
  if (!contractorType) return true;
  return contractorType.includes(ContractorType.SOLAR);
};

export const hasSOL = (account: InstallerAccount): boolean => {
  const contractorType = account?.contractor_type;
  if (!contractorType) return true;
  return contractorType.includes(ContractorType.STORAGE);
};

/** True for installers that do Solar exclusively (no Flex in contractor_type). */
export const isOnlySolar = (account: InstallerAccount): boolean =>
  hasSolar(account) && !hasFlex(account);

export const isOnlySOL = (account: InstallerAccount): boolean => {
  const contractorType = account?.contractor_type;
  if (!contractorType) return true;
  return (
    contractorType.includes(ContractorType.STORAGE) &&
    !contractorType.includes(ContractorType.FLEX) &&
    !contractorType.includes(ContractorType.SOLAR)
  );
};

export const isFlexOnly = (account: InstallerAccount): boolean =>
  hasFlex(account) && !hasSolar(account);

/** True when any contractor type is Solar, Storage, or EV Chargers. */
export const isContractorTypeEnergy = (account: InstallerAccount): boolean =>
  getContractorTypes(account).some((cType) =>
    ENERGY_CONTRACTOR_TYPES.has(cType)
  );
